import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AgGridModule } from 'ag-grid-angular';
import {
  CellClickedEvent,
  ColDef,
  RowClassRules,
  ValueGetterParams,
} from 'ag-grid-community';
import {
  getCancellationFlowHistoryLabel,
  getCancellationFlowHistoryStatusClass,
} from 'src/app/_enums/cancellation/cancellation-history-status.enum';
import { getInsuranceFlowChannelLabel } from 'src/app/_enums/insurance-flow/insurance-flow-channel.enum';
import {
  getInsuranceFlowHistoryLabel,
  getInsuranceFlowHistoryStatusClass,
  getLeadTypeFromHistoryStatus,
  InsuranceFlowHistoryStatus,
  InsuranceFlowStateHistoryStatus,
} from 'src/app/_enums/insurance-flow/insurance-flow-history-status.enum';
import { InsuranceFlowStatus } from 'src/app/_enums/insurance-flow/insurance-flow-status.enum';
import { LeadType } from 'src/app/_enums/lead-type.enum';
import { getSignatureTypeLabel } from 'src/app/_enums/signature-type.enum';
import { fmtUtcDateTime } from 'src/app/_helpers/utils';
import { CancellationFlowHistoryEntry } from 'src/app/_types/cancellation.types';
import { InsuranceFlowFullHistoryElement } from 'src/app/_types/insurance-flow.types';
import { BadgeRendererComponent } from 'src/app/shared/components/ag-grid-renderers/badge-renderer/badge-renderer.component';

const DIFFABLE_STATUSES = new Set<InsuranceFlowStateHistoryStatus>([
  InsuranceFlowStatus.POLICY_UPDATED,
  InsuranceFlowStatus.CONTRACT_UPDATED,
  InsuranceFlowStatus.OFFER_UPDATED,
  InsuranceFlowStatus.LEAD_UPDATED,
  InsuranceFlowStatus.OFFER_SIGNED,
  InsuranceFlowHistoryStatus.OFFER_CREATED,
  InsuranceFlowHistoryStatus.OFFER_SENT,
  InsuranceFlowHistoryStatus.OFFER_REJECTED,
]);

/**
 * A row of an affaire's log: an entry of the flow's own history, or of the
 * timeline of a cancellation ending it, named by the cancellation's id.
 */
export type TaskLogRow =
  | (InsuranceFlowFullHistoryElement & { kind: 'flow' })
  | (CancellationFlowHistoryEntry & {
      kind: 'cancellation';
      displayId: string;
    });

@Component({
  selector: 'app-task-log-table',
  standalone: true,
  imports: [AgGridModule],
  templateUrl: './task-log-table.component.html',
  styleUrl: './task-log-table.component.css',
})
export class TaskLogTableComponent {
  @Input() logsList: TaskLogRow[] = [];

  @Output() entryClicked = new EventEmitter<TaskLogRow>();

  protected readonly columnDefs: ColDef<TaskLogRow>[] = [
    {
      headerName: $localize`ID affaire`,
      field: 'displayId',
      filter: 'agTextColumnFilter',
      sortable: true,
      cellClass: 'bold-text-cell',
      minWidth: 120,
    },
    {
      headerName: $localize`ID Client`,
      field: 'customerDisplayId',
      filter: 'agTextColumnFilter',
      sortable: true,
      cellClass: 'simple-text-cell',
      minWidth: 130,
      valueGetter: (params: ValueGetterParams<TaskLogRow>) =>
        params.data?.kind === 'flow' ? params.data.customerDisplayId : '-',
    },
    {
      headerName: $localize`N° police`,
      field: 'policyNumber',
      filter: 'agTextColumnFilter',
      sortable: true,
      cellClass: 'simple-text-cell',
      minWidth: 120,
      valueGetter: (params: ValueGetterParams<TaskLogRow>) =>
        params.data?.kind === 'flow' &&
        getLeadTypeFromHistoryStatus(params.data.status) === LeadType.POLICY
          ? params.data.policyNumber?.trim() || '-'
          : '-',
    },
    {
      headerName: $localize`Type EVT`,
      field: 'status',
      filter: 'agTextColumnFilter',
      sortable: true,
      cellClass: 'simple-text-cell',
      minWidth: 170,
      valueGetter: (params: ValueGetterParams<TaskLogRow>) =>
        this.getStatusLabel(params.data),
    },
    {
      headerName: $localize`Date`,
      field: 'updatedOn',
      sortable: true,
      minWidth: 150,
      valueGetter: (params: ValueGetterParams<TaskLogRow>) =>
        this.getDisplayDate(params.data),
    },
    {
      headerName: $localize`Par`,
      field: 'updatedByFullName',
      filter: 'agTextColumnFilter',
      sortable: true,
      cellClass: 'simple-text-cell',
      minWidth: 150,
      valueGetter: (params: ValueGetterParams<TaskLogRow>) =>
        this.getDisplayName(params.data),
    },
    {
      headerName: $localize`Statut`,
      field: 'status',
      filter: 'agTextColumnFilter',
      sortable: true,
      minWidth: 170,
      cellRenderer: BadgeRendererComponent,
      cellRendererParams: {
        badgeClass: (row: TaskLogRow) =>
          row.kind === 'cancellation'
            ? getCancellationFlowHistoryStatusClass(row.status)
            : getInsuranceFlowHistoryStatusClass(row.status),
      },
      valueGetter: (params: ValueGetterParams<TaskLogRow>) =>
        this.getStatusLabel(params.data),
    },
    {
      headerName: $localize`Canal`,
      field: 'source',
      filter: 'agTextColumnFilter',
      sortable: true,
      cellClass: 'simple-text-cell',
      minWidth: 110,
      valueGetter: (params: ValueGetterParams<TaskLogRow>) =>
        this.getSourceLabel(params.data),
    },
    {
      headerName: $localize`Canal SIG`,
      field: 'signatureType',
      filter: 'agTextColumnFilter',
      sortable: true,
      cellClass: 'simple-text-cell',
      minWidth: 130,
      valueGetter: (params: ValueGetterParams<TaskLogRow>) => {
        const row = params.data;
        if (row?.kind !== 'flow') return '-';
        const leadType = getLeadTypeFromHistoryStatus(row.status);
        return leadType === LeadType.SUBMISSION || leadType === LeadType.POLICY
          ? this.getSignatureType(row)
          : '';
      },
    },
    {
      headerName: $localize`Canal envoi`,
      field: 'channel',
      filter: 'agTextColumnFilter',
      sortable: true,
      cellClass: 'simple-text-cell',
      minWidth: 130,
      valueGetter: (params: ValueGetterParams<TaskLogRow>) =>
        this.getChannelLabel(params.data),
    },
  ];

  protected readonly defaultColDef: ColDef<TaskLogRow> = {
    filter: true,
    sortable: false,
    resizable: true,
    autoHeight: true,
    flex: 1,
  };

  protected readonly rowClassRules: RowClassRules<TaskLogRow> = {
    'task-log-row-clickable': (params) => this.isClickable(params.data),
  };

  protected onCellClicked(event: CellClickedEvent<TaskLogRow>): void {
    if (!event.data || !this.isClickable(event.data)) {
      return;
    }

    this.entryClicked.emit(event.data);
  }

  /** A flow entry the diff modal can compare, or a cancellation entry that edited fields. */
  private isClickable(row?: TaskLogRow): boolean {
    if (!row) {
      return false;
    }

    return row.kind === 'cancellation'
      ? row.changes.length > 0
      : DIFFABLE_STATUSES.has(row.status);
  }

  private getDisplayDate(element?: TaskLogRow): string {
    const rawDate = element?.updatedOn ?? element?.endedOn;
    return rawDate ? fmtUtcDateTime(rawDate) : '-';
  }

  private getDisplayName(element?: TaskLogRow): string {
    return (
      element?.updatedByFullName?.trim() ||
      element?.endedByFullName?.trim() ||
      '-'
    );
  }

  private getSourceLabel(element?: TaskLogRow): string {
    if (element?.kind !== 'flow' || !element.source) {
      return '-';
    }

    switch (element.source) {
      case 'DIGITAL':
        return $localize`Digital`;
      case 'PHYSICAL':
        return $localize`Physique`;
      default:
        return element.source;
    }
  }

  private getSignatureType(element: InsuranceFlowFullHistoryElement): string {
    if (!element.signatureType) {
      return '-';
    }

    return getSignatureTypeLabel(element.signatureType) ?? '-';
  }

  private getChannelLabel(element?: TaskLogRow): string {
    if (!element?.channel) {
      return '-';
    }

    return getInsuranceFlowChannelLabel(element.channel) ?? '-';
  }

  private getStatusLabel(element?: TaskLogRow): string {
    if (!element?.status) {
      return '-';
    }

    return element.kind === 'cancellation'
      ? getCancellationFlowHistoryLabel(element.status)
      : (getInsuranceFlowHistoryLabel(element.status) ?? '-');
  }
}
